import { useMemo } from 'react';
import { useSession } from '@/features/auth/model/use-session';

/**
 * The roles in the current access token.
 *
 * ⚠️ For **affordances only**. Never a control.
 *
 * The API authorises every request by policy (§12.2) and is the only authority; this exists so the
 * panel does not offer a button whose request is guaranteed to come back 403. Hiding an action the
 * operator cannot take is the same courtesy as not offering a retry on a permission failure — it is
 * not a security boundary, and nothing here is trusted by anything.
 *
 * A JWT is not encrypted, so reading its claims client-side reveals nothing the holder does not
 * already have. It is also unverified here, which is exactly why the result may only decide what is
 * drawn: a forged token would show an operator buttons that then fail on the server.
 */

/** The role names the API issues (§23.1). Kept here because the panel's affordances key off them. */
export const SystemRoles = {
  SystemAdministrator: 'system-administrator',
  HeadOfControl: 'head-of-control',
  DeskControl: 'desk-control',
  CommitteeMember: 'committee-member',
  FieldVolunteer: 'field-volunteer',

  /**
   * قائد الميدان — the ninth role (§23.6, answered 18 August 2026). The Knight award is the only
   * thing it carries, so it appears in exactly one affordance below.
   */
  FieldLeader: 'field-leader',
} as const;

/**
 * The signed-in operator's own user id, from the token.
 *
 * ⚠️ Same standing as {@link useRoles}: for **affordances only**. It answers "is this row me?" so a
 * screen can offer the handover to the person actually holding a mission rather than to everybody
 * looking at it. The server decides whether the act is allowed.
 *
 * ⚠️ The id is the one claim of identity §12.8 keeps in the token — no name, no phone, no national
 * id. A screen that needs the name asks the API for it.
 */
export function useCurrentUserId(): string | null {
  const { session } = useSession();

  return useMemo(() => readSubject(session?.accessToken), [session?.accessToken]);
}

export function useRoles(): {
  roles: readonly string[];
  hasAny: (...roles: readonly string[]) => boolean;
} {
  const { session } = useSession();

  const roles = useMemo(() => readRoles(session?.accessToken), [session?.accessToken]);

  return {
    roles,
    hasAny: (...wanted) => wanted.some((role) => roles.includes(role)),
  };
}

/**
 * The roles carried by a token that is not the current session's — the one just returned by the OTP
 * exchange, before anything is stored.
 *
 * ⚠️ Same standing as {@link useRoles}: an **affordance**, never a control. The dashboard uses it to
 * turn away somebody whose work is in the app rather than letting them in to ten screens that all
 * refuse them, and a forged token would gain exactly nothing by getting past it — every request is
 * still authorised by policy on the server (§12.2).
 */
export function rolesIn(accessToken: string | undefined): readonly string[] {
  return readRoles(accessToken);
}

/**
 * Pulls the role claims out of a JWT payload without verifying it.
 *
 * The claim is `ClaimTypes.Role`, which .NET writes as the long schema URI, and a single role arrives
 * as a string rather than an array — both shapes have to be handled or a user with exactly one role
 * appears to have none.
 */
function readRoles(accessToken: string | undefined): readonly string[] {
  const claims = readClaims(accessToken);
  if (!claims) {
    return [];
  }

  const claim =
    claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ?? claims['role'];

  if (typeof claim === 'string') {
    return [claim];
  }

  return Array.isArray(claim) ? claim.filter((role): role is string => typeof role === 'string') : [];
}

/**
 * The payload of an unverified JWT.
 *
 * ⚠️ A malformed token is not a crash: the panel simply shows no privileged affordance, and the next
 * request fails on its own with a message the operator can read.
 */
function readClaims(accessToken: string | undefined): Record<string, unknown> | null {
  const payload = accessToken?.split('.')[1];
  if (!payload) {
    return null;
  }

  try {
    // base64url → base64. `atob` rejects the URL-safe alphabet, and padding is optional in a JWT.
    return JSON.parse(atob(payload.replaceAll('-', '+').replaceAll('_', '/'))) as Record<
      string,
      unknown
    >;
  } catch {
    return null;
  }
}

/** The `sub` claim, unverified — see {@link useCurrentUserId}. */
function readSubject(accessToken: string | undefined): string | null {
  const claims = readClaims(accessToken);
  const subject = claims?.['sub'] ?? claims?.['nameid'];

  return typeof subject === 'string' ? subject : null;
}
